import { useMemo, useRef } from 'react';

import { useApiClient } from '@@common';

import { ContextCodeClient, DesignerChatSessionClient } from '../../../api/api';
import { ChatAgentOption, ChatMentionSuggestion, ChatParticipant, PromptChatAdapter } from '../../../components/Chat/PromptChatAdapter';
import { createDesignerChatAdapter } from '../../../components/Chat/createDesignerChatAdapter';
import { designerChatScopes } from '../../../components/Chat/useDesignerChatAdapter';

const chatArtifactAttributeNames = {
	operationId: 'OperationId',
	eventHandlerId: 'EventHandlerId',
	integrationEventId: 'IntegrationEventId',
	entityId: 'EntityId',
	enumId: 'EnumId',
	fileId: 'FileId',
	fileName: 'FileName',
} as const;

export interface CodeGenArtifactContext {
	operationId?: string;
	eventHandlerId?: string;
	integrationEventId?: string;
	entityId?: string;
	enumId?: string;
	fileId?: string;
	fileName?: string;
}

type AdapterState = {
	key: string;
	agents?: ChatAgentOption[];
	agentsPromise?: Promise<ChatAgentOption[]>;
	activeAgentId?: string;
	sessionAgentIds: Map<string, string>;
};

/** Builds a chat adapter backed by the unified designer-session API. */
export function useCodeGenChatAdapter(contextId: string, artifactContext: CodeGenArtifactContext = {}): PromptChatAdapter {
	const sessionClient = useApiClient(DesignerChatSessionClient);
	const codeClient = useApiClient(ContextCodeClient);
	const stateRef = useRef<AdapterState>({ key: '', sessionAgentIds: new Map<string, string>() });
	const artifactContextRef = useRef(artifactContext);
	artifactContextRef.current = artifactContext;
	const stateKey = contextId;
	if (stateRef.current.key !== stateKey) {
		stateRef.current = { key: stateKey, sessionAgentIds: new Map<string, string>() };
	}

	return useMemo<PromptChatAdapter>(() => {
		const loadAgents = async (): Promise<ChatAgentOption[]> => {
			const state = stateRef.current;
			if (state.agents) {
				return state.agents;
			}

			state.agentsPromise ??= sessionClient
				.getAgents(designerChatScopes.contextCode, 'domain')
				.then((agents) => {
					const mapped = (agents ?? []).map((agent) => ({
						id: agent.id,
						name: agent.name,
						description: agent.name,
						isDefault: agent.isDefault,
					}));
					state.agents = mapped;
					state.activeAgentId ??= mapped.find((agent) => agent.isDefault)?.id;
					return mapped;
				})
				.catch((error) => {
					state.agentsPromise = undefined;
					throw error;
				});
			return state.agentsPromise;
		};

		const resolveAgentId = async (requestedAgentId?: string, sessionId?: string): Promise<string> => {
			const state = stateRef.current;
			const agentId = requestedAgentId ?? (sessionId ? state.sessionAgentIds.get(sessionId) : undefined) ?? state.activeAgentId;
			if (agentId) {
				state.activeAgentId = agentId;
				return agentId;
			}

			const defaultAgentId = (await loadAgents()).find((agent) => agent.isDefault)?.id;
			if (!defaultAgentId) {
				throw new Error('No default agent is available for this code chat.');
			}

			state.activeAgentId = defaultAgentId;
			return defaultAgentId;
		};

		const rememberSessionAgent = (sessionId: string, agentId?: string) => {
			if (agentId) {
				stateRef.current.sessionAgentIds.set(sessionId, agentId);
			}
		};

		return createDesignerChatAdapter({
			getContextAttributes: () => ({ ContextId: contextId, ...Object.fromEntries(
				Object.entries(chatArtifactAttributeNames).flatMap(([key, attribute]) => {
					const value = artifactContextRef.current[key as keyof CodeGenArtifactContext];
					return value ? [[attribute, value]] : [];
				}),
			) }),
			targetId: contextId,
			isAvailable: !!contextId,
			retryableSends: false,
			missingTargetMessage: 'A context is required to start this chat.',
			getAgents: loadAgents,
			sessionClient: {
				async createOrGetSession(_targetId, request) {
					const agentId = await resolveAgentId(request.agentId);
					const session = await sessionClient.createOrGetSession(designerChatScopes.contextCode, agentId, {
						forceNew: request.forceNew,
						targetId: contextId,
						contextScope: 'domain',
					});
					rememberSessionAgent(session.sessionId, session.agentId ?? agentId);
					return session;
				},
				async getSessions(_targetId, pageIndex, pageSize) {
					const result = await sessionClient.getScopedSessions(designerChatScopes.contextCode, contextId, 'domain', undefined, undefined, pageIndex, pageSize);
					(result.items ?? []).forEach((session) => rememberSessionAgent(session.sessionId, session.agentId));
					return result;
				},
				async updateSession(_targetId, sessionId, request) {
					const agentId = await resolveAgentId(undefined, sessionId);
					await sessionClient.updateSession(designerChatScopes.contextCode, agentId, sessionId, contextId, 'domain', undefined, undefined, request);
				},
				async deleteSession(_targetId, sessionId) {
					const agentId = await resolveAgentId(undefined, sessionId);
					await sessionClient.deleteSession(designerChatScopes.contextCode, agentId, sessionId, contextId, 'domain', undefined, undefined);
					stateRef.current.sessionAgentIds.delete(sessionId);
				},
				async addParticipants(_targetId, sessionId, userIds) {
					const agentId = await resolveAgentId(undefined, sessionId);
					await sessionClient.addParticipants(designerChatScopes.contextCode, agentId, sessionId, contextId, 'domain', undefined, undefined, { userIds });
				},
				async getParticipants(_targetId, sessionId): Promise<ChatParticipant[]> {
					const agentId = await resolveAgentId(undefined, sessionId);
					return sessionClient.getParticipants(designerChatScopes.contextCode, agentId, sessionId, contextId, 'domain', undefined, undefined);
				},
				async removeParticipant(_targetId, sessionId, userId) {
					const agentId = await resolveAgentId(undefined, sessionId);
					await sessionClient.removeParticipant(designerChatScopes.contextCode, agentId, sessionId, userId, contextId, 'domain', undefined, undefined);
				},
				async getMessagesDelta(_targetId, sessionId, afterMessageId, afterSequence, changeToken, signal) {
					const agentId = await resolveAgentId(undefined, sessionId);
					return sessionClient.getMessagesDelta(
						designerChatScopes.contextCode,
						agentId,
						sessionId,
						contextId,
						'domain',
						undefined,
						undefined,
						afterMessageId,
						afterSequence,
						changeToken,
						signal,
					);
				},
				async getMessagesBefore(_targetId, sessionId, beforeSequence, pageSize) {
					const agentId = await resolveAgentId(undefined, sessionId);
					return sessionClient.getMessagesBefore(
						designerChatScopes.contextCode,
						agentId,
						sessionId,
						contextId,
						'domain',
						undefined,
						undefined,
						beforeSequence,
						pageSize,
					);
				},
				async getAttachmentDownloadUrl(sessionId, fileId) {
					const result = await sessionClient.getAttachmentDownload(sessionId, fileId);
					return result.url;
				},
				async getAttachmentHtml(sessionId, fileId, signal) {
					const result = await sessionClient.getAttachmentHtml(sessionId, fileId, signal);
					return result.html;
				},
			},
			async instruct(instruction, sessionId, conversationName, forceNew, requestedAgentId, additionalMessageAttributes) {
				const agentId = await resolveAgentId(requestedAgentId, sessionId);
				const currentArtifact = artifactContextRef.current;
				const messageAttributes = Object.fromEntries(
					Object.entries({
						[chatArtifactAttributeNames.operationId]: currentArtifact.operationId,
						[chatArtifactAttributeNames.eventHandlerId]: currentArtifact.eventHandlerId,
						[chatArtifactAttributeNames.integrationEventId]: currentArtifact.integrationEventId,
						[chatArtifactAttributeNames.entityId]: currentArtifact.entityId,
						[chatArtifactAttributeNames.enumId]: currentArtifact.enumId,
						[chatArtifactAttributeNames.fileId]: currentArtifact.fileId,
						[chatArtifactAttributeNames.fileName]: currentArtifact.fileName,
						...additionalMessageAttributes,
					}).filter((entry): entry is [string, string] => Boolean(entry[1])),
				);
				const response = await sessionClient.instruct(designerChatScopes.contextCode, agentId, {
					instruction,
					sessionId,
					conversationName,
					forceNew,
					targetId: contextId,
					contextScope: 'domain',
					messageAttributes,
				});
				rememberSessionAgent(response.sessionId, agentId);
				return response;
			},
			async searchMentions(query: string): Promise<ChatMentionSuggestion[]> {
				const results = await codeClient.searchArtifacts(contextId, query, undefined, undefined);
				return (results ?? []).map((result) => ({ name: result.name, type: result.type }));
			},
		});
	}, [codeClient, contextId, sessionClient]);
}
